import React from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Tooltip } from '../../../../global/tooltip/tooltip';
import NoteIcon from '../../../../../images/svg/sf-order-note';
import {
    ISalesforceOrderDTO,
    ISalesforceOrderProductDTO,
    ISalesforceOrderRecordDTO,
} from '../../../../../api/salesforce-order.dao';
import {
    EMPTY_VALUE,
    getProductsGrandTotal,
    ISalesforceOrderDisplay,
    linkedEventDisplayValues,
    mapOrderToDisplay,
    toNumber,
} from './salesforce-order-display.mapper';
import {
    GROUP_DESCRIPTION,
    GROUP_POST_EVENT,
    GROUP_PRODUCTS,
    IOrderFieldDescriptor,
    LINK_PREVIEW_ORDER_FIELDS,
    ORDER_FIELD_GROUPS,
    PRODUCTS_CURRENCY_LABEL,
    PRODUCTS_EMPTY_TEXT,
    PRODUCTS_PRICE_BOOK_LABEL,
    PRODUCTS_TOTAL_LABEL,
    PRODUCT_COLUMNS,
    FIELD_OEC_URL_LINK_TEXT,
} from './salesforce-order.texts';
import {
    BILLING_TIME_SENSITIVITY_KEY,
    CANCELLATION_OTHER_KEY,
    HIGH_PROFILE_ATTENDEE_DETAILS_KEY,
    HIGH_PROFILE_REASONING_KEY,
    isBillingTimeSensitivityVisible,
    isCancellationFieldKey,
    isCancellationOtherVisible,
    isCancellationVisibleForStatus,
    isHighProfileAttendeeDetailsVisible,
    isHighProfileReasoningVisible,
    isContractNumberHidden,
    isPostEventVisibleForStatus,
    getRecordTypeFromOrder,
} from './order-field-visibility';

const NumberCell = (props: { value: string | number | null; fractionDigits?: number; maxFractionDigits?: number }) => {
    const num = toNumber(props.value);
    if (num == null) {
        return <>{EMPTY_VALUE}</>;
    }
    const digits = props.fractionDigits ?? 2;
    return <FormattedNumber value={num} minimumFractionDigits={digits} maximumFractionDigits={props.maxFractionDigits ?? digits} />;
};

const visibleGroupsForStatus = (record: ISalesforceOrderRecordDTO) => {
    const showCancellation = isCancellationVisibleForStatus(record.orderStatus);
    const showPostEvent = isPostEventVisibleForStatus(record.orderStatus);
    const showCancellationOther = isCancellationOtherVisible(record.cancellationReason);
    const recordType = getRecordTypeFromOrder(record);
    return ORDER_FIELD_GROUPS
        .filter(group => group.title !== GROUP_POST_EVENT || showPostEvent)
        .map(group => ({
            ...group,
            fields: group.fields.flatMap(field => {
                if (isContractNumberHidden(field.key, recordType)) {
                    return [];
                }
                if (field.key === HIGH_PROFILE_REASONING_KEY) {
                    return isHighProfileReasoningVisible(record.highProfileOrder) ? [field] : [];
                }
                if (field.key === HIGH_PROFILE_ATTENDEE_DETAILS_KEY) {
                    return isHighProfileAttendeeDetailsVisible(record.highProfileReasoning, record.highProfileOrder) ? [field] : [];
                }
                if (field.key === BILLING_TIME_SENSITIVITY_KEY) {
                    return isBillingTimeSensitivityVisible(record.billingMethod) ? [field] : [];
                }
                if (!isCancellationFieldKey(field.key)) {
                    return [field];
                }
                if (!showCancellation) {
                    return [];
                }
                return field.key === CANCELLATION_OTHER_KEY
                    ? (showCancellationOther ? [field] : [])
                    : [field];
            }),
        }));
};

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const FieldValue = (props: { fieldKey: string; value: string }) => {
    if (props.fieldKey === 'feedbackFormUrl' && isHttpUrl(props.value)) {
        return (
            <a
                className='salesforce-order__field-link'
                href={props.value}
                target='_blank'
                rel='noopener noreferrer'
                data-test-id='salesforce-order-feedback-form-link'
            >
                {props.value}
            </a>
        );
    }
    if (props.fieldKey === 'oeCentralUrl' && isHttpUrl(props.value)) {
        return (
            <a
                className='salesforce-order__field-link'
                href={props.value}
                target='_blank'
                rel='noopener noreferrer'
                data-test-id='salesforce-order-oec-url-link'
            >
                <FormattedMessage {...FIELD_OEC_URL_LINK_TEXT} />
            </a>
        );
    }
    return <>{props.value || EMPTY_VALUE}</>;
};

const OrderBody = (props: {
    display: ISalesforceOrderDisplay;
    record: ISalesforceOrderRecordDTO;
    extraFields?: IOrderFieldDescriptor[];
}) => {
    const intl = useIntl();
    return (
        <div className='salesforce-order__groups' data-test-id='salesforce-order-details'>
            {visibleGroupsForStatus(props.record).map((group, index) => {
                const fields = index === 0 && props.extraFields?.length
                    ? [...group.fields, ...props.extraFields]
                    : group.fields;
                return (
                <section className='salesforce-order__group' key={group.title.id}>
                    {index > 0 && <hr className='salesforce-order__divider' />}
                    <h3 className='salesforce-order__group-title'>{intl.formatMessage(group.title)}</h3>
                    <dl
                        className='salesforce-order__fields'
                        style={{ gridTemplateColumns: `repeat(${group.columns}, 1fr)` }}
                    >
                        {fields.map(field => field.type === 'placeholder' ? (
                            <div
                                className='salesforce-order__field'
                                key={field.key}
                                data-test-id='salesforce-order-contract-placeholder'
                            />
                        ) : (
                            <div
                                className={
                                    'salesforce-order__field' +
                                    (field.fullWidth ? ' salesforce-order__field--full-width' : '') +
                                    (field.wide ? ' salesforce-order__field--wide' : '') +
                                    (field.startsRow ? ' salesforce-order__field--row-start' : '')
                                }
                                key={field.key}
                            >
                                <dt className='salesforce-order__field-label'>
                                    {`${intl.formatMessage(field.label)}:`}
                                </dt>
                                <dd className={
                                    'salesforce-order__field-value' +
                                    (field.preserveWhitespace ? ' salesforce-order__field-value--pre-wrap' : '')
                                }>
                                    <FieldValue fieldKey={field.key} value={props.display[field.key] || EMPTY_VALUE} />
                                </dd>
                            </div>
                        ))}
                    </dl>
                </section>
                );
            })}
        </div>
    );
};

const DescriptionSection = (props: { description: string }) => (
    <section className='salesforce-order__group' data-test-id='salesforce-order-description'>
        <hr className='salesforce-order__divider' />
        <h3 className='salesforce-order__group-title'>
            <FormattedMessage {...GROUP_DESCRIPTION} />
        </h3>
        <p className='salesforce-order__description-text'>{props.description || EMPTY_VALUE}</p>
    </section>
);

const ProductsSection = (props: {
    products: ISalesforceOrderProductDTO[];
    priceBook: string;
    currency: string;
}) => {
    const intl = useIntl();
    const { products, priceBook, currency } = props;

    return (
        <section className='salesforce-order__group' data-test-id='salesforce-order-products'>
            <hr className='salesforce-order__divider' />
            <h3 className='salesforce-order__group-title'>{intl.formatMessage(GROUP_PRODUCTS)}</h3>

            <div className='salesforce-order__products-body'>
            <dl className='salesforce-order__fields' style={{ gridTemplateColumns: '1fr fit-content(33%)', columnGap: '10px' }}>
                <div className='salesforce-order__field'>
                    <dt className='salesforce-order__field-label'>
                        <FormattedMessage {...PRODUCTS_PRICE_BOOK_LABEL} />
                    </dt>
                    <dd className='salesforce-order__field-value'>{priceBook || EMPTY_VALUE}</dd>
                </div>
                <div className='salesforce-order__field salesforce-order__field--end'>
                    <dt className='salesforce-order__field-label'>
                        <FormattedMessage {...PRODUCTS_CURRENCY_LABEL} />
                    </dt>
                    <dd className='salesforce-order__field-value'>{currency || EMPTY_VALUE}</dd>
                </div>
            </dl>

            {products.length === 0 ? (
                <p className='salesforce-order__products-empty' data-test-id='salesforce-order-products-empty'>
                    <FormattedMessage {...PRODUCTS_EMPTY_TEXT} />
                </p>
            ) : (
                <table className='salesforce-order__products-table'>
                    <thead>
                        <tr>
                            <th className='salesforce-order__col-product'>{intl.formatMessage(PRODUCT_COLUMNS.product)}</th>
                            <th className='salesforce-order__col-number'>{intl.formatMessage(PRODUCT_COLUMNS.listPrice)}</th>
                            <th className='salesforce-order__col-number'>{intl.formatMessage(PRODUCT_COLUMNS.unitPrice)}</th>
                            <th className='salesforce-order__col-note'>{intl.formatMessage(PRODUCT_COLUMNS.notes)}</th>
                            <th className='salesforce-order__col-qty'>{intl.formatMessage(PRODUCT_COLUMNS.quantity)}</th>
                            <th className='salesforce-order__col-number'>{intl.formatMessage(PRODUCT_COLUMNS.total)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((product, index) => (
                            <tr key={`${product.productName}-${index}`}>
                                <td className='salesforce-order__col-product'>{product.productName || EMPTY_VALUE}</td>
                                <td className='salesforce-order__col-number'><NumberCell value={product.listPrice} /></td>
                                <td className='salesforce-order__col-number'><NumberCell value={product.unitPrice} /></td>
                                <td className='salesforce-order__col-note'>
                                    {product.notes ? (
                                        <Tooltip content={product.notes} className='salesforce-order__note-tooltip'>
                                            <span className='salesforce-order__note-icon' data-test-id='salesforce-order-note-icon'>
                                                <NoteIcon />
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </td>
                                <td className='salesforce-order__col-qty'><NumberCell value={product.quantity} fractionDigits={0} maxFractionDigits={2} /></td>
                                <td className='salesforce-order__col-number'><NumberCell value={product.total} /></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className='salesforce-order__products-total-row'>
                            <td className='salesforce-order__col-product'>
                                <FormattedMessage {...PRODUCTS_TOTAL_LABEL} />
                            </td>
                            <td className='salesforce-order__col-number' />
                            <td className='salesforce-order__col-number' />
                            <td className='salesforce-order__col-note' />
                            {/* Span the # and Total columns so the total has room and aligns to the right edge. */}
                            <td className='salesforce-order__col-number' colSpan={2}>
                                {currency ? `${currency} ` : ''}
                                <NumberCell value={getProductsGrandTotal(products)} />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            )}
            </div>
        </section>
    );
};

const SalesforceOrderViewDetails = (props: { order: ISalesforceOrderDTO; showLinkedEventFields?: boolean }) => {
    const record = props.order.order;
    if (!record) {
        return null;
    }
    const display = props.showLinkedEventFields
        ? { ...mapOrderToDisplay(record), ...linkedEventDisplayValues(props.order) }
        : mapOrderToDisplay(record);
    return (
        <>
            <OrderBody
                display={display}
                record={record}
                extraFields={props.showLinkedEventFields ? LINK_PREVIEW_ORDER_FIELDS : undefined}
            />
            <DescriptionSection description={display.description} />
            <ProductsSection
                products={props.order.products}
                priceBook={display.pricebook}
                currency={record.currencyIsoCode || ''}
            />
        </>
    );
};

export { SalesforceOrderViewDetails };
